import type { MedalTier } from "@dailywars/core/medals";
import { Link } from "@tanstack/react-router";
import { Video } from "lucide-react";
import type * as React from "react";

import { FlameIcon, flameKindForStreak } from "#/components/FlameIcon";
import { MedalIcon } from "#/components/MedalIcon";
import { PlayerPreviewName } from "#/components/PlayerPreviewName";
import { Button } from "#/components/ui/button";
import { Infotip, InfotipContent, InfotipTrigger } from "#/components/ui/infotip";
import { cn } from "#/lib/utils";

export type LeaderboardRow = {
  displayName: string;
  score: number;
  playerId: string;
  dailyStreak: number;
  replayId?: string;
};

function LeaderboardEntryName({
  entry,
  isYou,
}: {
  entry: { displayName: string; playerId: string };
  isYou: boolean;
}) {
  return (
    <>
      <PlayerPreviewName playerId={entry.playerId} displayName={entry.displayName} />
      {isYou ? <span className="text-muted-foreground"> (You)</span> : null}
    </>
  );
}

function LeaderboardStreakFlame({
  dailyStreak,
  variantLabel,
}: {
  dailyStreak: number;
  variantLabel: "Standard" | "Premium";
}) {
  const kind = flameKindForStreak(dailyStreak);
  if (!kind) return null;

  const label = `${variantLabel} Streak: ${dailyStreak}`;
  return (
    <Infotip>
      <InfotipTrigger aria-label={label} className="inline-flex shrink-0">
        <FlameIcon kind={kind} size={16} />
      </InfotipTrigger>
      <InfotipContent>{label}</InfotipContent>
    </Infotip>
  );
}

function LeaderboardReplayLink({ replayId }: { replayId: string }) {
  return (
    <Button
      variant="accent"
      size="icon-sm"
      render={<Link to="/replay/$replayId" params={{ replayId }} preload={false} />}
      nativeButton={false}
      aria-label="Watch replay"
    >
      <Video />
    </Button>
  );
}

function LeaderboardRowPlayer({
  entry,
  rank,
  isYou,
  medal,
  medalsVisible,
  variantLabel,
}: {
  entry: LeaderboardRow;
  rank: number;
  isYou: boolean;
  medal: MedalTier | undefined;
  medalsVisible: boolean;
  variantLabel: "Standard" | "Premium";
}) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-foreground">
      {medalsVisible ? (
        <span className="inline-flex w-5 shrink-0 justify-center">
          {medal ? <MedalIcon tier={medal} size={18} /> : null}
        </span>
      ) : null}
      <span className="min-w-0 truncate">
        <span className="tabular-nums text-muted-foreground">{rank}. </span>
        <LeaderboardEntryName entry={entry} isYou={isYou} />
      </span>
      <LeaderboardStreakFlame dailyStreak={entry.dailyStreak} variantLabel={variantLabel} />
    </span>
  );
}

function LeaderboardRowScore({ score, replayId }: { score: number; replayId: string | undefined }) {
  return (
    <span className="flex shrink-0 items-center gap-2">
      <span className="tabular-nums text-muted-foreground">
        {score} {score === 1 ? "day" : "days"}
      </span>
      {replayId ? <LeaderboardReplayLink replayId={replayId} /> : null}
    </span>
  );
}

export function LeaderboardVirtualRow({
  virtualRow,
  entry,
  medal,
  viewerPlayerId,
  medalsVisible,
  variantLabel,
  showReplayLinks,
}: {
  virtualRow: { key: React.Key; index: number; size: number; start: number };
  entry: LeaderboardRow;
  medal: MedalTier | undefined;
  viewerPlayerId: string | undefined;
  medalsVisible: boolean;
  variantLabel: "Standard" | "Premium";
  showReplayLinks: boolean;
}) {
  const isYou = viewerPlayerId !== undefined && entry.playerId === viewerPlayerId;
  return (
    <div
      key={virtualRow.key}
      className={cn(
        "absolute left-0 top-0 flex w-full items-center justify-between gap-3 border-b border-border/60 px-4 py-3 text-sm",
        isYou && "bg-primary/10",
      )}
      style={{
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      <LeaderboardRowPlayer
        entry={entry}
        rank={virtualRow.index + 1}
        isYou={isYou}
        medal={medal}
        medalsVisible={medalsVisible}
        variantLabel={variantLabel}
      />
      <LeaderboardRowScore
        score={entry.score}
        replayId={showReplayLinks ? entry.replayId : undefined}
      />
    </div>
  );
}
